import { observable } from "mobx";
import { api, api_adm, apiOperacao } from "../../api";
import { toast } from "react-toastify";
import { downloadBinaryFile, downloadFromBase64, toast_error } from "../../utils";

export class _BotsAdminStore {

  obs = observable({
    tabelaBots: [] as any,
    currentPageBot: 1 as number,
    length_bots: 0 as number,
    tabelaRelatorios: [] as any,
    currentPageRelatorios: 1 as number,
    length_relatorios: 0 as number,
    tasks_relatorio: [] as any[],
    length_tasks_relatorio: 0 as number,
    open_erros_relatorio: false as boolean,

    sefa_erros: [] as any[],
    length_sefa_erros: 0 as number,
  })

  /**
  * Busca a lista de bots/tarefas de acordo com os filtros informados.
  *
  * Envia uma requisição POST para a API administrativa responsável por listar
  * os bots. Após receber a resposta, atualiza a tabela exibida na tela e também
  * a quantidade total de registros encontrados.
  *
  * Em caso de erro na requisição, exibe uma mensagem informando que não foi
  * possível carregar os bots.
  *
  * @async
  * @param {any} params - Parâmetros/filtros utilizados para buscar os bots.
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  async handleGetBots(params) {
    try {
      let resp = await api_adm.post(`/api/admin/bots/index`, params);

      this.obs.tabelaBots = [];
      this.obs.length_bots = 0;

      if(resp.data.itens.length > 0) {

        this.obs.tabelaBots = resp.data.itens
        this.obs.length_bots = resp.data.count

      }

    } catch (ex) {
      toast.error("Erro ao carregar Bots!", {});
    }
  }

  /**
  * Reinicia uma ou mais tarefas/bots.
  *
  * Envia uma requisição POST para a API responsável por reiniciar bots,
  * utilizando os parâmetros recebidos. Caso a resposta da API retorne uma lista
  * vazia, considera que a reinicialização foi executada com sucesso e exibe uma
  * mensagem para o usuário.
  *
  * Em caso de erro na requisição, exibe uma mensagem de falha e retorna um array
  * vazio como valor padrão.
  *
  * @async
  * @param {any} params - Parâmetros utilizados para reiniciar as tarefas/bots.
  * @returns {Promise<any[]>} Retorna os dados da resposta da API ou um array vazio em caso de erro.
  */

  async handleRestartBots(params) {

    try {

      let resp = await api_adm.post(`/api/admin/bots/restart`, params);

      if (resp.data.length == 0) {

        toast.success("Reiniciada a(s) tarefa(s)!", {});

      }

      return resp.data

    } catch (ex) {
      toast.error("Erro ao carregar Bots!", {});
    }

    return []

  }

  /**
  * Atualiza as informações/configurações de um bot no painel administrativo.
  *
  * Envia uma requisição PUT para a API de administração com os dados recebidos
  * em `params`. Caso a API retorne `ok: true`, exibe uma mensagem de sucesso.
  * Caso contrário, exibe uma mensagem de erro.
  *
  * @async
  * @param {Object} params - Dados do bot que serão enviados para atualização.
  * @returns {Promise<void>} Não retorna valor diretamente.
  * @throws {Error} Relança o erro capturado caso a requisição falhe.
  */

  async handleUpdateBot(params) {

    try {

      const resp = await api_adm.put(`/api/admin/bots/update`, params);

      if (resp.data?.ok === true) {

        toast.success("JSON atualizado com sucesso.");

      } else {

        toast.error("Erro ao atualizar Bot!");

      }

    } catch (ex) {

      toast.error("Erro ao atualizar Bot!", {});

      throw ex;

    }
  }

  /**
  * Executa uma ação de download caso uma condição seja atendida.
  *
  * Verifica a condição informada e, se ela for verdadeira, executa a função
  * recebida em `acao`. Caso a condição seja falsa, exibe uma mensagem de erro
  * informando que não foi possível baixar o arquivo.
  *
  * Esse método é útil para centralizar validações simples antes de realizar
  * downloads, evitando repetir a mesma lógica de erro em vários pontos do código.
  *
  * @param {boolean} condicao - Condição que define se a ação poderá ser executada.
  * @param {Function} acao - Função que será executada quando a condição for verdadeira.
  * @returns {void}
  */
  baixarOuErro = (condicao, acao) => {
    if (condicao) acao();
    else toast.error("Não foi possível baixar o arquivo!");
  };

  /**
  * Realiza o download de arquivos relacionados a uma tarefa/bot, de acordo com o tipo informado.
  *
  * O método identifica o tipo da tarefa e chama o endpoint correspondente para
  * buscar o arquivo disponível. Dependendo do tipo, o download pode ser feito
  * a partir de um arquivo em Base64 ou de uma resposta binária/blob.
  *
  * Alguns tipos são ignorados por não possuírem fluxo de download nesta rotina.
  * Para tipos não suportados, uma mensagem de erro é exibida ao usuário.
  *
  * Em caso de falha em qualquer etapa da requisição ou processamento do arquivo,
  * exibe uma mensagem genérica informando que o download não pôde ser realizado.
  *
  * @async
  * @param {string} tipo - Tipo da tarefa/bot utilizado para definir o fluxo de download.
  * @param {any} row - Dados da linha/tarefa selecionada, contendo IDs e informações necessárias para o download.
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  async handleDownloadFile(tipo, row) {

    try {

      const tiposIgnorados = [
        "caixapostal", "calculo", "contestacao", "manifestacao",
        "solicitacao_sefa", "xml_integration", "dec_ler", "dec_download"
      ];

      if (tiposIgnorados.includes(tipo)) return;

      if (["caixa", "tst", "receita", "sefa"].includes(tipo)) {

        const resp = await apiOperacao("cnds").post(`/cnds/download`, { id: row.cnds_id });

        this.baixarOuErro(resp.status === 200 && resp.data.file, () =>

          downloadFromBase64(resp.data.file, "pdf", `${row.cnpj}_${tipo}.pdf`)

        );

      } else if (tipo == "fgts_digital") {

        for (const subtipo of ["guia", "relatorio"]) {

          const resp = await apiOperacao("fgts").post(`/fgts/download`, {
            id: row.fgtsdigital_id,
            tipo: subtipo
          });

          this.baixarOuErro(resp.status === 200 && resp.data.file, () =>

            downloadFromBase64(resp.data.file, "pdf", `${row.cnpj}_${subtipo}.pdf`)

          );

        }

      } else if (tipo == "parauapebas") {

        const resp = await apiOperacao("prefeitura").get(`/prefeitura/arquivos/${row.paraupebas_id}`);

        if (resp.status === 200 && resp.data.length) {

          for (const file of resp.data) {

            const fileResp = await apiOperacao("prefeitura").get(`/prefeitura/download/${file.download}`);

            this.baixarOuErro(fileResp.status === 200 && fileResp.data.file, () =>

              downloadFromBase64(fileResp.data.file, "zip", `${row.cnpj}_${file.download}`)

            );

          }

        } else {

          toast.warning('Sem arquivos Disponíveis.');

        }

      } else if (["pgdas", "gera_das"].includes(tipo)) {

        const resp = await apiOperacao("pgdas").post(`/pgdas/consulta-arquivo-pgdas-tarefa`, { uuid: row.pgdas_id });

        if (resp.status === 200 && resp.data.length) {

          for (const arquivo of resp.data) {

            const fileResp = await apiOperacao("pgdas").post(`/pgdas/download-one-pgdas`, {
              uuid: row.pgdas_id,
              file_id: arquivo.id
            });

            downloadFromBase64(fileResp.data.base64, "pdf", `${row.cnpj}_${arquivo.nome}`);
          }

        } else {

          toast.error("Não foi possível baixar o arquivo!");

        }

      } else if (tipo == "relatorio") {

        const resp = await apiOperacao("relatorios").post(`/relatorios/task/download`,
          { id: row.relatorio_id, format: row.format },
          { responseType: 'blob' }
        );

        this.baixarOuErro((resp.status === 200 && resp.data.type !== 'application/json'), () =>

          downloadBinaryFile(resp.data, `${row.cnpj}_RELATORIO.${row.format}`)

        );

      } else if (tipo == "download_sefa") {

        let resp = await apiOperacao("sefa").post(`/sefa/download/zip`,
          { nome: row._id, files: row.arquivos },
          { responseType: 'blob' }
        );

        this.baixarOuErro((resp.status === 200 && resp.data.type !== 'application/json'), () =>

          downloadBinaryFile(resp.data, `${row.cnpj}_${row._id}.zip`)

        );

      } else if (tipo == "sitfis") {

        const resp = await apiOperacao("sitfis").post(`/sitfis/download`, { id: row.sitfis_id });

        this.baixarOuErro(resp.status === 200 && resp.data.file, () =>

          downloadFromBase64(resp.data.file, "pdf", `${row.cnpj}_SITFIS.pdf`)

        );

      } else if (tipo == "sped_pva" || tipo == "sped_contabil") {

        const resp = await apiOperacao("sped").get(`/sped/task/download/${row.sped_id}`);

        this.baixarOuErro(resp.status === 200 && resp.data.file, () =>

          downloadFromBase64(resp.data.file, "txt", `${row.cnpj}-SPED.txt`)

        );

      } else if (tipo == "dae_avulso") {

        const resp = await apiOperacao("dae_avulso").post(`/dae_avulso/download`, { task_id: row.task_id });

        this.baixarOuErro(resp.status === 200 && resp.data.base64, () =>

          downloadFromBase64(resp.data.base64, "pdf", `${row.cnpj}_DAE-AVULSO.pdf`)

        );

      } else if (tipo == "simples") {

        let receitas = row.receitas.map(x => {

          return Object.entries(x).map(([campo, valor]) => {

            return campo

          })

        })

        const resp = await apiOperacao("dei").post(`/dei/download`,
          { task_id: row.task_id, receitas: receitas },
          { responseType: 'blob' }
        );

        this.baixarOuErro((resp.status === 200 && resp.data.type !== 'application/json'), () =>

          downloadBinaryFile(resp.data, `${row.cnpj}_${row._id}.zip`)

        );

      } else if (tipo == "tarefas_danfe") {

        const resp = await apiOperacao("notas_xml").get(`/notas_xml/danfe-task-download/${row._id}`,
          { responseType: "blob" }
        );

        this.baixarOuErro((resp.status === 200 && resp.data.type !== 'application/json'), () =>

          downloadBinaryFile(resp.data, `${row.cnpj}_${row._id}.zip`)

        );

      } else if (["sped_validar", "sped_transmitir"].includes(tipo)) {

        const resp = await apiOperacao("sped").get(`/sped/transmissoes/download/${row.sped_id}`,
          { responseType: "blob" }
        );

        this.baixarOuErro((resp.status === 200 && resp.data.type !== 'application/json'), () =>

          downloadBinaryFile(resp.data, `${row.cnpj}_${row.sped_id}.pdf`)

        );

      } else {

        toast.error(`Tipo de download não suportado: ${tipo}`);

      }

    } catch (ex) {

      toast_error(ex, `Não foi possível baixar o arquivo, tente novamente mais tarde.`);

    }

  }

  /**
  * Busca todas as tarefas/bots que correspondem aos filtros informados.
  *
  * Realiza consultas paginadas na API administrativa até recuperar todos os
  * registros disponíveis. A cada página, adiciona as tarefas encontradas em
  * uma lista consolidada contendo apenas os dados necessários para ações em
  * massa, como reinício de tarefas.
  *
  * A busca utiliza um limite fixo de 500 registros por página e continua
  * enquanto a API retornar itens. A execução é interrompida quando:
  *
  * - a API não retorna mais itens;
  * - a quantidade retornada é menor que o limite da página;
  * - ocorre algum erro durante a consulta.
  *
  * Em caso de erro, exibe uma mensagem ao usuário e retorna as tarefas que
  * já haviam sido carregadas até o momento.
  *
  * @async
  * @param {any} baseParams - Parâmetros base utilizados para filtrar a busca das tarefas.
  * @returns {Promise<Array<{ id: any, tipo: any, status: any }>>} Lista com todas as tarefas encontradas.
  */

  async handleGetAllBots(baseParams) {

    const all_tasks = [];
    const limit = 500;

    let page = 1;

    while (true) {

      try {

        const params = {
          ...baseParams,
          page,
          limit
        };

        let resp = await api_adm.post(`/api/admin/bots/index`, params);

        const itens = resp?.data?.itens ?? [];

        if (itens.length === 0) break;

        for (const item of itens) {

          all_tasks.push({
            id: item._id,
            tipo: baseParams.tipo,
            status: item.status
          });

        }

        if (itens.length < limit) break;

        page++;

      } catch (error) {

        toast.error("Erro ao consultar todas tarefas, tente novamente mais tarde.");
        break;

      }

    }

    return all_tasks;

  }

  async handleExportPlanilha(id: string, task_id: string, cnpj: string, tipo: string, tipo_p: number, status: string, finalizado: string, erro: string, dataInicial: string, dataFinal: string, conta_id: string) {
    
    try {

      let params = {
        id: id,
        task_id: task_id,
        cnpj: cnpj,
        tipo: tipo,
        tipo_p: tipo_p,
        status: status,
        finalizado: finalizado,
        erro: erro,
        data_inicial: dataInicial,
        data_final: dataFinal,
        conta_id: conta_id
      }

      let resp = await api_adm.post(`/api/admin/bots/export-tasks`, params);

      if (resp.data.length == 0) {

        toast.success("Excel gerado", {});

      }

      return resp.data

    } catch (ex) {
      toast.error("Erro ao gerar o Excel!", {});
    }

  }

  async handleTasksPaged(paged: number, cnpj: string, data_inicio: string, data_fim: string) {
    try {

      let data = {
        page: paged,
        limit: 15,
        cnpj: cnpj,
        date_start: data_inicio,
        date_end: data_fim
      };

      let resp = await api_adm.post("/api/admin/bots/index-relatorios", data);

      this.obs.tasks_relatorio = resp.data.itens || [];
      this.obs.length_tasks_relatorio = resp.data.count || 0;

    } catch (ex) {

      toast.error("Erro ao Carregar Relatórios, tente novamente mais tarde.", {});

    }
  }

  /**
   * Carrega a listagem paginada de erros da SEFA.
   *
   * Permite filtrar os registros por CNPJ, período e descrição do erro.
   * Atualiza a lista de erros e a quantidade total de registros observáveis.
   *
   * @param page Página atual da listagem.
   * @param filter_cnpj CNPJ utilizado no filtro.
   * @param date1 Data inicial do período (YYYY-MM-DD).
   * @param date2 Data final do período (YYYY-MM-DD).
   * @param filter_erro Texto para filtrar a descrição do erro.
   * @returns {Promise<void>}
   */
  async handleIndexSefaErros(
    page: number,
    filter_cnpj: string,
    date1: string,
    date2: string,
    filter_erro: string,
  ) {

    try {

      let params = {
        page: page,
        limit: 20,
        cnpj: filter_cnpj.replace(/\D/g, ""),
        date_start: date1,
        date_end: date2,
        erro: filter_erro
      }

      let resp = await api_adm.post("/api/admin/bots/index/sefa-erros", params);

      this.obs.sefa_erros = []
      this.obs.length_sefa_erros = 0

      if (resp.data.itens) {

        this.obs.sefa_erros = resp.data.itens
        this.obs.length_sefa_erros = resp.data.count

      }

    } catch (ex) {

      toast.error("Erro ao Carregar Relatórios, tente novamente mais tarde.", {});

    }

  }

  /**
   * Obtém os dados de uma solicitação da SEFA pelo identificador.
   *
   * @param id Identificador da solicitação.
   * @returns {Promise<any>} Dados da solicitação ou um array vazio caso ocorra erro.
   */
  async handleIndexSefaSolicitacao(id: string) {

    try {

      let resp = await api_adm.get(`/api/admin/bots/index/sefa-solicitacao/${id}`);

      if(resp.status == 200) {
        return resp.data
      }

    } catch (ex) {

      toast.error("Erro ao carregar solicitação, tente novamente mais tarde.", {});

    }

    return []

  }

}

export const BotsAdminStore = new _BotsAdminStore();
